import React, { useState, useEffect } from "react";
import "./AddBlogSEO.css";
import "./AddProductSEO.css";
import axios from "axios";
import InputField from "../../../Components/UI-Controls/InputField/InputField";
import placeholderImage from "../../../Assets/soho.jpg";
import { IoImageOutline } from "react-icons/io5";
import { security_key } from "../../../Services/Api";

export default function AddBlogSEO({
  isOpen,
  onClose,
  onSave,
  initialData,        // metaData object from AddBlogs
  mainBlog,           // { image, title, author, status } for header blocks
  blogId,             // row._id of the blog being edited
  Url,                // base URL to prefix relative image paths, if needed
}) {
  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    canonicalUrl: "",
    robots: "",
    ogTitle: "",
    ogDescription: "",
    xTitle: "",
    xDescription: "",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (initialData) {
      setSeoData({
        metaTitle: initialData.metaTitle || "",
        metaDescription: initialData.metaDescription || "",
        metaKeywords: initialData.metaKeywords || "",
        canonicalUrl: initialData.canonicalUrl || "",
        robots: initialData.robots || "",
        ogTitle: initialData.ogTitle || "",
        ogDescription: initialData.ogDescription || "",
        xTitle: initialData.xTitle || "",
        xDescription: initialData.xDescription || "",
      });
    }
    setSaveError(null);
  }, [initialData, isOpen]);

  const handleFieldChange = (name, value) => {
    setSeoData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCopyToOg = () => {
    setSeoData((prev) => ({
      ...prev,
      ogTitle: prev.metaTitle,
      ogDescription: prev.metaDescription,
    }));
  };

  const handleCopyToX = () => {
    setSeoData((prev) => ({
      ...prev,
      xTitle: prev.metaTitle,
      xDescription: prev.metaDescription,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!blogId) {
      setSaveError("Missing blog id — cannot save SEO.");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const token = sessionStorage.getItem("authToken");
    const headers = {
      Authorization: `Bearer ${token}`,
      "x-api-key": security_key,
    };

    // Map local field names to what the API controller expects
    const payload = {
      title: seoData.metaTitle,
      description: seoData.metaDescription,
      keywords: seoData.metaKeywords,
      robots: seoData.robots,
      og_title: seoData.ogTitle,
      og_description: seoData.ogDescription,
      x_title: seoData.xTitle,
      x_description: seoData.xDescription,
    };

    try {
      const response = await axios.put(
        `${Url}/api/v1/blogs/manage-blog-seo/${blogId}`,
        payload,
        { headers }
      );

      const savedMeta = response?.data?.data || {};

      // Map API response (title/description/keywords/...) back to local field names
      // so the parent's metaData state stays consistent with this component's shape.
      const mappedMeta = {
        metaTitle: savedMeta.title || "",
        metaDescription: savedMeta.description || "",
        metaKeywords: savedMeta.keywords || "",
        canonicalUrl: savedMeta.canonical_url || "",
        robots: savedMeta.robots || "",
        ogTitle: savedMeta.og_title || "",
        ogDescription: savedMeta.og_description || "",
        og_image: savedMeta.og_image || "",
        xTitle: savedMeta.x_title || "",
        xDescription: savedMeta.x_description || "",
        x_image: savedMeta.x_image || "",
      };

      onSave && onSave(mappedMeta);
    } catch (err) {
      const errMsg = err?.response?.data?.message || err.message || "Failed to save SEO settings.";
      setSaveError(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const renderImagePicker = (label, imageUrl, onPick, onCancel) => (
    <div className="blog_seo_image_picker">
      <label>{label}</label>
      {imageUrl ? (
        <div className="blog_seo_image_preview_wrapper">
          <img
            src={/^https?:\/\//i.test(imageUrl) ? imageUrl : `${Url || ""}${imageUrl}`}
            alt={label}
            className="blog_seo_image_preview"
          />
          <button type="button" className="blog_seo_image_cancel" onClick={onCancel}>
            &times;
          </button>
        </div>
      ) : (
        <div className="blog_seo_image_upload_box" onClick={onPick}>
          <IoImageOutline size={32} color="#555" />
          <span>Click to select image</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="seo_drawer_overlay" onClick={onClose}>
      <div className="blog_seo_drawer_content" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="blog_seo_drawer_header">
          <h2>Blog SEO Details</h2>
          <div className="blog_seo_header_details_section">
            <div className="blog_seo_header_img">
              <img src={mainBlog?.image || placeholderImage} alt="" />
            </div>
            <div className="blog_seo_header_meta">
              <h1>{mainBlog?.title || "Untitled Blog"}</h1>
              <p>{mainBlog?.author || ""}</p>
              {mainBlog?.status ? (
                <div className="blog_seo_header_status">{mainBlog.status}</div>
              ) : null}
            </div>
          </div>
          <button className="blog_seo_close_btn" onClick={onClose}>&times;</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="blog_seo_drawer_form">

          {saveError && (
            <div className="blog_seo_section">
              <div style={{ color: "#c0392b", fontWeight: 600 }}>{saveError}</div>
            </div>
          )}

          <div className="blog_seo_section">
            <h3>General SEO</h3>
            <div className="blog_seo_field_group">
              <label>Meta Title</label>
              <InputField
                value={seoData.metaTitle}
                name="metaTitle"
                onChange={(e) => handleFieldChange("metaTitle", e.target.value)}
                placeholder="Enter meta title"
                width="100%"
              />
            </div>
            <div className="blog_seo_field_group">
              <label>Meta Description</label>
              <textarea
                className="blog_seo_textarea"
                value={seoData.metaDescription}
                onChange={(e) => handleFieldChange("metaDescription", e.target.value)}
                placeholder="Enter meta description"
                rows={3}
              />
            </div>
          </div>

          <div className="blog_seo_section">
            <h3>Search Keywords</h3>
            <div className="blog_seo_field_group">
              <label>Keywords (Comma Separated)</label>
              <InputField
                value={seoData.metaKeywords}
                name="metaKeywords"
                onChange={(e) => handleFieldChange("metaKeywords", e.target.value)}
                placeholder="e.g. furniture care, patio sets, outdoor sofas"
                width="100%"
              />
            </div>
          </div>

          <div className="blog_seo_section">
            <div className="blog_seo_section_header_row">
              <h3>Open Graph (Facebook/LinkedIn)</h3>
              <p onClick={handleCopyToOg}>Copy from Meta</p>
            </div>
            <div className="blog_seo_field_group">
              <label>OG Title</label>
              <InputField
                value={seoData.ogTitle}
                name="ogTitle"
                onChange={(e) => handleFieldChange("ogTitle", e.target.value)}
                placeholder="Enter OG title"
                width="100%"
              />
            </div>
            <div className="blog_seo_field_group">
              <label>OG Description</label>
              <textarea
                className="blog_seo_textarea"
                value={seoData.ogDescription}
                onChange={(e) => handleFieldChange("ogDescription", e.target.value)}
                placeholder="Enter OG description"
                rows={3}
              />
            </div>
          
          </div>

          <div className="blog_seo_section">
            <div className="blog_seo_section_header_row">
              <h3>X (Twitter) Card Details</h3>
              <p onClick={handleCopyToX}>Copy from Meta</p>
            </div>
            <div className="blog_seo_field_group">
              <label>X Title</label>
              <InputField
                value={seoData.xTitle}
                name="xTitle"
                onChange={(e) => handleFieldChange("xTitle", e.target.value)}
                placeholder="Enter X title"
                width="100%"
              />
            </div>
            <div className="blog_seo_field_group">
              <label>X Description</label>
              <textarea
                className="blog_seo_textarea"
                value={seoData.xDescription}
                onChange={(e) => handleFieldChange("xDescription", e.target.value)}
                placeholder="Enter X description"
                rows={3}
              />
            </div>
          
          </div>

       
          {/* Sticky Footer */}
          <div className="blog_seo_drawer_footer">
            <button type="button" className="blog_seo_cancel_btn" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button type="submit" className="blog_seo_save_btn" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save SEO Settings"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}